import { FileUp } from "lucide-react";
import { useRef, useState, type FC } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { CompaniesAPI } from "../api/companiesApi";
import { refreshCompanies } from "../queries";
import type { ImportReport, ImportRowStatus } from "../types";

const COLUMNS =
  "company, legal_name, fein, dba, street, city, state, zip, country, naics, us_employees, signatory_name, signatory_title, signatory_email, contact_name, contact_title, contact_email, contact_phone, contact_roles, employee_email, job_title";

const STATUS: Record<ImportRowStatus, { label: string; className: string }> = {
  ok: { label: "Ready", className: "text-success" },
  unmatched: { label: "No case", className: "text-warn" },
  skipped: { label: "Skipped", className: "text-warn" },
  error: { label: "Error", className: "text-danger" }
};

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

type ImportedCompany = ImportReport["companies"][number];

const changes = (c: ImportedCompany): boolean =>
  c.action === "create" ||
  c.employees.length > 0 ||
  Object.values(c.added).some(Boolean);

const changeSummary = (c: ImportedCompany): string => {
  const adds = [
    c.added.entities && plural(c.added.entities, "entity", "entities"),
    c.added.contacts && plural(c.added.contacts, "contact", "contacts"),
    c.added.job_descriptions && plural(c.added.job_descriptions, "job", "jobs")
  ].filter(Boolean);
  const parts = [
    adds.length ? `Adds ${adds.join(", ")}` : null,
    c.employees.length
      ? `${adds.length ? "links" : "Links"} ${plural(
          c.employees.length,
          "case",
          "cases"
        )}`
      : null
  ].filter(Boolean);
  return parts.join(" · ") || "Nothing new";
};

type Props = { open: boolean; onOpenChange: (open: boolean) => void };

export const ImportDialog: FC<Props> = ({ open, onOpenChange }) => {
  const [csv, setCsv] = useState("");
  const [report, setReport] = useState<ImportReport | null>(null);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const close = (next: boolean) => {
    if (!next) {
      setCsv("");
      setReport(null);
    }
    onOpenChange(next);
  };

  const run = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const out = await CompaniesAPI.importCsv(csv, dryRun);
      if (dryRun) {
        setReport(out);
      } else {
        const linked = out.companies.reduce(
          (n, c) => n + (c.linked?.length ?? 0),
          0
        );
        toast.success(
          `Imported ${plural(
            out.companies.filter(changes).length,
            "company",
            "companies"
          )} and linked ${plural(linked, "case", "cases")}`
        );
        void refreshCompanies();
        close(false);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't read that file"
      );
    } finally {
      setBusy(false);
    }
  };

  const problems = report?.rows.filter((r) => r.status !== "ok") ?? [];
  const changing = report?.companies.filter(changes).length ?? 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-2xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Import Companies</DialogTitle>
          <DialogDescription>
            A CSV with one row per company, entity, contact or employee. Rows
            with the same company build one record; employees are matched to
            their cases by email. HRIS connections (Workday, Rippling, BambooHR)
            come later.
          </DialogDescription>
        </DialogHeader>

        {report ? (
          <div className="flex flex-col gap-3" data-testid="import-preview">
            <div className="rounded-md border border-line-2">
              {report.companies.map((c) => (
                <div
                  key={c.name}
                  className="flex items-center gap-3 border-b border-line-2 px-3 py-2 text-sm last:border-b-0"
                >
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {c.name}
                  </span>
                  <Badge
                    variant={c.action === "create" ? "default" : "secondary"}
                  >
                    {c.action === "create" ? "New" : "Update"}
                  </Badge>
                  <span className="text-sm text-fg-3">{changeSummary(c)}</span>
                </div>
              ))}
            </div>
            {problems.length ? (
              <div className="max-h-40 overflow-y-auto rounded-md border border-line-2">
                {problems.map((r) => (
                  <div
                    key={r.row}
                    className="flex gap-3 border-b border-line-2 px-3 py-1.5 text-sm last:border-b-0"
                  >
                    <span className="w-14 shrink-0 text-fg-3 tabular-nums">
                      Row {r.row}
                    </span>
                    <span
                      className={cn(
                        "w-16 shrink-0 font-medium",
                        STATUS[r.status].className
                      )}
                    >
                      {STATUS[r.status].label}
                    </span>
                    <span className="min-w-0 flex-1">{r.message}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Textarea
              aria-label="CSV"
              rows={8}
              className="font-mono text-sm"
              placeholder={`company,legal_name,fein,contact_name,contact_email,employee_email\nNorthwind,Northwind Labs Inc.,12-3456789,Dana Cho,dana@northwind.com,priya@example.com`}
              value={csv}
              onChange={(e) => setCsv(e.currentTarget.value)}
            />
            <div className="flex items-center gap-2">
              <input
                ref={file}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                data-testid="import-file"
                onChange={(e) => {
                  const f = e.currentTarget.files?.[0];
                  if (f) void f.text().then(setCsv);
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => file.current?.click()}
              >
                <FileUp /> Choose file
              </Button>
              <span className="text-sm text-fg-3">Columns: {COLUMNS}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          {report ? (
            <>
              <Button variant="secondary" onClick={() => setReport(null)}>
                Back
              </Button>
              <Button
                variant="primary"
                disabled={busy || !changing}
                onClick={() => void run(false)}
              >
                Import {plural(changing, "company", "companies")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={busy || !csv.trim()}
                onClick={() => void run(true)}
              >
                Preview
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
